import React from 'react';
import { Button, Icon } from '@packages/ui/shared';
import { createUseStyles } from 'react-jss';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import colors from '@packages/core/styles/colors';

const useStyles = createUseStyles({
    container: {
        display: 'flex',
        gap: 4,
    },
    tooltip: {
        minWidth: 50,
        marginTop: 5,
        '& .tooltip-inner': {
            backgroundColor: colors.brandSecondary,
            borderRadius: 4,
            fontSize: '13px',
            fontWeight: 400,
        },
    },
});

interface ActionItem {
    name: string;
    icon: string;
    tooltip: string;
    isVisible: boolean;
    isDisabled: boolean;
    isLoading?: boolean;
    onPress: () => void;
    testId: string;
}

interface CidCvnTableActionsProps {
    isDownloadButtonDisabled: boolean;
    isDownloading: boolean;
    isRetryButtonDisabled: boolean;
    isRetrying: boolean;
    isViewCaseButtonDisabled: boolean;
    showRetryButton: boolean;
    showViewCaseButton?: boolean;
    onDownload: () => void;
    onRetry: () => void;
    onViewCase: () => void;
}

export function CidCvnTableActions({
    isDownloadButtonDisabled,
    isDownloading,
    isRetryButtonDisabled,
    isRetrying,
    isViewCaseButtonDisabled,
    showRetryButton = false,
    showViewCaseButton = false,
    onDownload,
    onRetry,
    onViewCase,
}: CidCvnTableActionsProps) {
    const { t } = useTranslation();
    const classes = useStyles();

    const actions: ActionItem[] = [
        {
            name: 'download',
            icon: 'sync-down',
            tooltip: 'cidCvnCheck:table.actionTooltips.download',
            isVisible: true,
            isDisabled: isDownloadButtonDisabled,
            isLoading: isDownloading,
            onPress: () => onDownload(),
            testId: 'download-button',
        },
        {
            name: 'retry',
            icon: 'refresh',
            tooltip: 'cidCvnCheck:table.actionTooltips.retryCheck',
            isVisible: showRetryButton,
            isDisabled: isRetryButtonDisabled,
            isLoading: isRetrying,
            onPress: () => onRetry(),
            testId: 'retry-button',
        },
        {
            name: 'view',
            icon: 'open-in-new',
            tooltip: 'cidCvnCheck:table.actionTooltips.viewTechlineCase',
            isVisible: showViewCaseButton,
            isDisabled: isViewCaseButtonDisabled,
            onPress: () => onViewCase(),
            testId: 'view-case-button',
        },
    ].filter((action) => action.isVisible);

    if (!actions.length) {
        return null;
    }

    return (
        <div className={classes.container}>
            {actions.map((action) => {
                return (
                    <OverlayTrigger
                        key={action.name}
                        placement="bottom"
                        overlay={
                            <Tooltip className={classes.tooltip} id={`${action.name}-tool-tip`}>
                                {t(action.tooltip)}
                            </Tooltip>
                        }
                    >
                        <Button
                            key={action.name}
                            data-testid={action.testId}
                            variant="ghost-blue"
                            disabled={action.isDisabled}
                            isLoading={!!action.isLoading}
                            onPress={action.onPress}
                        >
                            <Icon name={action.icon} color="blueOne" />
                        </Button>
                    </OverlayTrigger>
                );
            })}
        </div>
    );
}
